import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tab, Tabs, TextField, Typography } from '@mui/material';

import {
  VOUCHER_TEXT_LANGUAGES,
  VoucherTextIssue,
  VoucherTextLanguage,
  VoucherTextScope,
} from '@bandido/shared/types';

import {
  VoucherTextsDraft,
  voucherTextCount,
  voucherTextId,
  voucherTextIssueKey,
  voucherTextLangsWithIssues,
} from '../voucherTexts';

const KEY = 'vouchers.config';

interface TextBlockBoxProps {
  scope: VoucherTextScope;
  blockKey: string;
  draft: VoucherTextsDraft;
  limit: number;
  issues: Record<string, VoucherTextIssue[]>;
  canEdit: boolean;
  /** Encabezado y ayuda de la caja, cuando la sección los pone. */
  title?: string;
  hint?: string;
  onChange: (scope: VoucherTextScope, key: string, lang: VoucherTextLanguage, body: string) => void;
}

/**
 * UNA CAJA de texto, con su pestaña de idioma, su contador y sus problemas.
 *
 * La comparten los textos de la agencia y los de cada tipo de producto: son la
 * misma caja contra el mismo validador, y escribirla dos veces era garantizar
 * que dentro de un mes contaran distinto.
 *
 * El idioma es estado de LA CAJA y no de la pantalla (decisión D4): el inglés
 * es opcional bloque por bloque —al emitir, un bloque sin traducir cae al
 * español y el resto sale en inglés—, así que un interruptor global mentiría
 * sobre cómo se imprime.
 */
export const TextBlockBox: FC<TextBlockBoxProps> = ({
  scope,
  blockKey,
  draft,
  limit,
  issues,
  canEdit,
  title,
  hint,
  onChange,
}) => {
  const { t } = useTranslation();
  const [lang, setLang] = useState<VoucherTextLanguage>('es');

  const body = draft[voucherTextId(scope, blockKey, lang)] ?? '';
  const problemas = issues[voucherTextId(scope, blockKey, lang)];
  const conProblemas = voucherTextLangsWithIssues(scope, blockKey, issues);
  const enIngles = draft[voucherTextId(scope, blockKey, 'en')] ?? '';
  const largo = voucherTextCount(body);
  const pasado = largo > limit;

  return (
    <Box>
      {title && <Typography variant="subtitle2">{title}</Typography>}
      {hint && (
        <Typography variant="body2" color="text.secondary">
          {hint}
        </Typography>
      )}

      <Tabs
        value={lang}
        onChange={(_event, value) => setLang(value as VoucherTextLanguage)}
        sx={{ minHeight: 36, mt: 1 }}
      >
        {VOUCHER_TEXT_LANGUAGES.map((code) => (
          <Tab
            key={code}
            value={code}
            // La pestaña avisa que el problema está adentro: sin esto, algo mal
            // en el idioma que no se está viendo apagaba el Guardar sin que
            // hubiera nada marcado en pantalla.
            sx={{ minHeight: 36, color: conProblemas.includes(code) ? 'error.main' : undefined }}
            label={
              // El inglés dice qué pasa si queda vacío: no es un idioma
              // "faltante", es la caída al español, bloque por bloque.
              code === 'es' || enIngles.trim()
                ? t(`${KEY}.texts.lang.${code}`)
                : t(`${KEY}.texts.lang.enEmpty`)
            }
          />
        ))}
      </Tabs>

      <TextField
        value={body}
        disabled={!canEdit}
        error={Boolean(problemas?.length) || pasado}
        multiline
        minRows={5}
        fullWidth
        /*
         * SIN `maxLength`: cortar en el navegador es peor que avisar. Estos
         * textos se pegan desde un documento legal, y un recorte silencioso
         * deja las condiciones a medias y sale impreso así. El contador dice
         * cuánto sobra y el Guardar queda apagado hasta que entre.
         */
        onChange={(event) => onChange(scope, blockKey, lang, event.target.value)}
        helperText={
          <Box component="span" display="flex" justifyContent="space-between" gap={2}>
            <Box component="span">
              {problemas?.length
                ? problemas.map((issue) => t(voucherTextIssueKey(issue), { max: limit })).join(' · ')
                : t(`${KEY}.texts.emptyRemoves`)}
            </Box>
            <Box component="span" sx={{ whiteSpace: 'nowrap' }} color={pasado ? 'error.main' : undefined}>
              {t(`${KEY}.texts.counter`, { count: largo, max: limit })}
            </Box>
          </Box>
        }
      />
    </Box>
  );
};
